import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  useNodeTopContainers,
  type ContainerOrderBy,
  type MetricRange,
} from '@/lib/metrics'
import { bytes, bps, pct } from '@/lib/format'

interface Props {
  nodeId: string
  range: MetricRange
}

const ORDER_TABS: { key: ContainerOrderBy; label: string }[] = [
  { key: 'cpu', label: 'CPU' },
  { key: 'mem', label: 'Memory' },
  { key: 'net_rx', label: 'Net in' },
  { key: 'net_tx', label: 'Net out' },
  { key: 'restart_count', label: 'Restarts' },
]

export function NodeContainerTop({ nodeId, range }: Props) {
  const [orderBy, setOrderBy] = useState<ContainerOrderBy>('cpu')
  const { data, isLoading } = useNodeTopContainers(nodeId, range, orderBy, 15)
  const rows = data?.containers ?? []

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">Top containers</CardTitle>
        <div className="flex gap-1">
          {ORDER_TABS.map((t) => (
            <Button
              key={t.key}
              variant={orderBy === t.key ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setOrderBy(t.key)}
            >
              {t.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <Skeleton className="h-32" />}
        {!isLoading && rows.length === 0 && (
          <div className="text-sm text-muted-foreground">
            No container samples in this window.
          </div>
        )}
        {!isLoading && rows.length > 0 && (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left py-1">Service</th>
                <th className="text-left">Container</th>
                <th className="text-right">CPU avg</th>
                <th className="text-right">CPU max</th>
                <th className="text-right">Mem</th>
                <th className="text-right">Net in</th>
                <th className="text-right">Net out</th>
                <th className="text-right">Restarts</th>
                <th className="text-left pl-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.container_id} className="border-t">
                  <td className="py-1 pr-2">
                    {c.service_id ? (
                      <Link
                        to={`/services/${c.service_id}`}
                        className="hover:underline"
                      >
                        <span className="font-medium">
                          {c.service_name ?? '—'}
                        </span>
                        {c.stack && (
                          <span className="text-muted-foreground">
                            {' · '}
                            {c.stack}
                          </span>
                        )}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="pr-2 font-mono text-[11px]">
                    {c.container_name ?? (
                      <span className="text-muted-foreground">
                        {c.container_id.slice(0, 12)}
                      </span>
                    )}
                  </td>
                  <td className="text-right">
                    {pct(c.avg_cpu_pct ?? null, 1)}
                  </td>
                  <td className="text-right">
                    {pct(c.max_cpu_pct ?? null, 1)}
                  </td>
                  <td className="text-right">
                    {bytes(c.avg_mem_used ?? null)}
                    {c.max_mem_limit ? (
                      <span className="text-muted-foreground">
                        {' / '}
                        {bytes(c.max_mem_limit)}
                      </span>
                    ) : null}
                  </td>
                  <td className="text-right">
                    {bps(c.avg_net_rx_bps ?? null)}
                  </td>
                  <td className="text-right">
                    {bps(c.avg_net_tx_bps ?? null)}
                  </td>
                  <td className="text-right">
                    {c.max_restart_count ?? 0}
                  </td>
                  <td className="pl-2">
                    {c.status ? (
                      <Badge
                        variant={c.status === 'running' ? 'outline' : 'destructive'}
                        className="text-[10px]"
                      >
                        {c.status}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  )
}
